import {
  DisplayMarket,
  Percent,
  PERCENT_ZERO,
  SKELETON_DISPLAY_MARKET,
} from "@silo/types";
import { cn } from "../../utils/class-names";
import { ReactNode } from "react";
import { TokenContext } from "../token-context";
import { AvailableToBorrow } from "./available-to-borrow";
import { AprTable } from "./apr-table";
import { getAssets } from "@silo/utils";
import { Array, pipe } from "effect";
import { CardPerks, mapToPerksAsset } from "./card-perks";
import { match, P } from "ts-pattern";
import { Warning } from "../warning";
import { DeprecatedChip, PausedChip, UnstableOracleChip } from "../alert-chip";
import { SkeletonWrapper } from "../skeleton";
import { LogoSize, logoSizeMap } from "../logo";

export const marketLogoSize: LogoSize = "lg";
export const tokenLogoSize: LogoSize = "sm";

export interface CardProps {
  className?: string;
  market: DisplayMarket;
  marketLogo: ReactNode;
  tokenLogo: ReactNode;
  revenueApr: Percent;
}

export function Card({
  className,
  market,
  marketLogo,
  tokenLogo,
  revenueApr,
}: CardProps): JSX.Element {
  const {
    chainKey,
    marketTokenAddress,
    marketSymbol,
    marketName,
    warningConfig,
    hasUnstableOracle,
    isPaused,
    isDeprecated,
  } = market;

  const perksAssets = pipe(
    getAssets(market),
    Array.map(mapToPerksAsset),
    Array.filter((asset) => asset.hasPerks)
  );

  const hasChip = hasUnstableOracle || isPaused || isDeprecated;

  return (
    <div
      data-cy="market-card-display"
      className={cn(
        "flex flex-col h-full gap-4 px-2.5 py-5 rounded-lg bg-shale-800",
        className
      )}
    >
      <div className="flex items-center justify-between gap-3">
        {/* min-w-0 makes elipsis overflow work */}
        <div className="flex items-center min-w-0 gap-3">
          {match(warningConfig)
            .with({ scope: P.union("all", "markets") }, ({ warningKey }) => (
              <Warning.TooltipWarning warningKey={warningKey} />
            ))
            .otherwise(() => null)}

          <TokenContext.Provider
            value={{ chainKey, tokenAddress: marketTokenAddress }}
          >
            {marketLogo}
          </TokenContext.Provider>

          <div className="flex flex-col">
            <span className="font-bold text-white">{marketSymbol}</span>
            <span className="overflow-hidden text-xs font-normal text-shale-200 whitespace-nowrap text-ellipsis">
              {marketName}
            </span>
          </div>
        </div>

        {hasChip && (
          <div className="flex items-center gap-1">
            {hasUnstableOracle && <UnstableOracleChip iconOnly />}
            {isPaused && <PausedChip iconOnly />}
            {isDeprecated && <DeprecatedChip iconOnly />}
          </div>
        )}
      </div>

      <div className="h-px bg-shale-750" />

      <AvailableToBorrow market={market} tokenLogo={tokenLogo} />

      <div className="h-px bg-shale-750" />

      <AprTable market={market} revenueApr={revenueApr} />

      {perksAssets.length > 0 && (
        <>
          <div className="h-px bg-shale-750" />

          <CardPerks.Root>
            {perksAssets.map((asset) => (
              <CardPerks.Asset asset={asset} />
            ))}
          </CardPerks.Root>
        </>
      )}
    </div>
  );
}

interface PulseCardProps {
  className?: string;
}

export function PulseCard({ className }: PulseCardProps): JSX.Element {
  return (
    <SkeletonWrapper className={cn("", className)}>
      <Card
        market={SKELETON_DISPLAY_MARKET}
        marketLogo={
          <div
            style={{
              width: logoSizeMap[marketLogoSize],
              height: logoSizeMap[marketLogoSize],
            }}
          ></div>
        }
        tokenLogo={
          <div
            style={{
              width: logoSizeMap[tokenLogoSize],
              height: logoSizeMap[tokenLogoSize],
            }}
          ></div>
        }
        revenueApr={PERCENT_ZERO}
      />
    </SkeletonWrapper>
  );
}
